import { Github, Linkedin, Twitter, Instagram } from "lucide-react";
import { BUSINESS } from "../lib/business";

const COLS = [
  {
    title: "Studio",
    links: [
      { label: "Services", href: "/#services" },
      { label: "Pricing", href: "/#pricing" },
      { label: "Process", href: "/#process" },
      { label: "Tech Stack", href: "/#tech-stack" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", href: "/about" },
      { label: "Testimonials", href: "/#testimonials" },
      { label: "FAQ", href: "/#faq" },
      { label: "Contact", href: "/contact" },
    ],
  },
  {
    title: "Disciplines",
    links: [
      { label: "Web & Apps", href: "/#services" },
      { label: "AI Products", href: "/#services" },
      { label: "Hardware & IoT", href: "/#services" },
      { label: "3D Printing", href: "/#services" },
    ],
  },
];

const SOCIALS = [
  { icon: Github, href: "https://github.com", label: "GitHub", id: "github" },
  { icon: Linkedin, href: "https://www.linkedin.com/in/devz-corner", label: "LinkedIn", id: "linkedin" },
  { icon: Twitter, href: "https://x.com", label: "Twitter", id: "twitter" },
  { icon: Instagram, href: "https://instagram.com", label: "Instagram", id: "instagram" },
];

export default function Footer() {
  return (
    <footer
      className="relative border-t border-white/5 bg-ink-0"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center">
                <img src="/logo.svg" alt="Devzcorner logo" className="w-full h-full object-contain" />
              </div>
              <span className="font-display text-2xl tracking-tight">Devzcorner</span>
            </div>
            <p className="text-zinc-400 max-w-sm leading-relaxed">
              End-to-end product engineering studio building software, AI systems,
              hardware, and 3D products from concept to launch.
            </p>
            <div className="mt-5 space-y-1.5 text-sm text-zinc-500 max-w-sm">
              <p className="text-zinc-400 font-medium">{BUSINESS.legalName}</p>
              <p>{BUSINESS.addressOneLine}</p>
              <p>
                <a href={BUSINESS.phoneHref} className="hover:text-white transition-colors">
                  {BUSINESS.phoneDisplay}
                </a>
                {" · "}
                <a href={`mailto:${BUSINESS.email}`} className="hover:text-white transition-colors">
                  {BUSINESS.email}
                </a>
              </p>
            </div>
            <div className="mt-5 flex flex-wrap gap-2 max-w-xs sm:max-w-none">
              {["Interface · Intelligence · Firmware · Fabrication", "Response within 24 hours"].map((item) => (
                <span
                  key={item}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full border border-white/10 text-zinc-500"
                >
                  {item}
                </span>
              ))}
            </div>
            <div className="mt-6 flex gap-2">
              {SOCIALS.map(({ icon: Icon, href, label, id }) => (
                <a
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${id}`}
                  className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 hover:bg-white/5 transition-all hover:-translate-y-1 hover:shadow-[0_0_15px_-3px_rgba(255,255,255,0.2)]"
                >
                  <Icon size={14} strokeWidth={2} />
                </a>
              ))}
            </div>
          </div>

          {/* Columns */}
          <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {COLS.map((c) => (
              <div key={c.title}>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
                  {c.title}
                </div>
                <ul className="space-y-3">
                  {c.links.map((l) => (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        className="text-sm text-zinc-400 hover:text-white transition-colors"
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Giant wordmark */}
        <div className="relative mt-20">
          <div
            aria-hidden
            className="font-display text-[min(9.2vw,7.5rem)] leading-none tracking-tight text-white/[0.05] whitespace-nowrap text-center"
          >
            DEVZCORNER
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-6 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 flex-wrap">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600">
            © 2026 Devzcorner. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <a href="/privacy" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-400 transition-colors">
              Privacy Policy
            </a>
            <a href="/terms" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-400 transition-colors">
              Terms & Conditions
            </a>
            <a href="/refund" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-400 transition-colors">
              Refund &amp; Cancellation
            </a>
            <a href="/shipping" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-400 transition-colors">
              Shipping & Delivery
            </a>
            <a href="/sitemap" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-400 transition-colors">
              Sitemap
            </a>
            <a href="/contact" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-400 transition-colors">
              Contact
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
